<script lang="ts" setup>
  import MainNav from '@/components/landingPage/nav/MainNav.vue'
  import GlobalBackground from '@/components/misc/GlobalBackground.vue'
  import GlobalSearchModal from '@/components/misc/GlobalSearchModal.vue'
  import KeyboardShortcutsModal from '@/components/misc/KeyboardShortcutsModal.vue'
  import DevPanel from '@/components/misc/DevPanel.vue'
  import DebugOverlay from '@/components/misc/DebugOverlay.vue'
  import NotifyAlertContainer from '@/components/misc/notifications/NotifyAlertContainer.vue'
  import FloatingCartButton from '@/components/misc/FloatingCartButton.vue'
  import CookieConsentBanner from '@/components/common/CookieConsentBanner.vue'
  import { computed, onMounted, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useJwtStore, useCartStore, useTaxConfigStore } from '@/stores'
  import { useTypedRoute } from '@/composables'
  import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts'
  import { useDevMode } from '@/composables/useDevMode'
  import { isClient } from '@/composables/useSSR'
  import { useRoute } from 'vue-router'

  // Keep <html lang> in sync with the active i18n locale (client only).
  const { locale } = useI18n()
  watch(
    locale,
    (lang) => {
      if (isClient && typeof document !== 'undefined' && lang) {
        document.documentElement.lang = lang
      }
    },
    { immediate: true }
  )

  // Initialize keyboard shortcuts
  useKeyboardShortcuts()

  // Hide main nav in dashboard (dashboard has its own sidebar nav) and print pages
  const currentRoute = useRoute()
  const isInDashboard = computed(() => currentRoute.path.startsWith('/dashboard') || currentRoute.path.startsWith('/webmail/'))
  const isPrintPage = computed(() => currentRoute.meta.isPrintPage === true)

  // Initialize dev mode (only active in development)
  const { isDevEnvironment } = useDevMode()

  // https://www.shutterstock.com/image-vector/abstract-sphere-shape-glowing-circles-particles-578229412
  // https://www.shutterstock.com/image-illustration/connection-lines-around-earth-globe-theme-557310703

  const route = useTypedRoute<{ id: string }>()
  const jwtStore = useJwtStore()
  const cartStore = useCartStore()
  const taxConfigStore = useTaxConfigStore()
  void jwtStore.getJwt // Access to trigger store initialization

  onMounted(() => {
    // Initialize cart and tax config from backend (non-blocking)
    cartStore.initCart()
    taxConfigStore.fetchConfig()
  })
</script>

<template>
  <!-- Print pages: minimal wrapper, no UI chrome -->
  <div v-if="isPrintPage" class="print-page">
    <router-view />
  </div>

  <!-- Normal pages: full app shell -->
  <v-app v-else>
    <a class="skip-link" href="#main-content">{{ $t('a11y.skipToContent') }}</a>
    <GlobalBackground v-if="!isInDashboard" />
    <!-- Hide main nav in dashboard - dashboard has its own sidebar nav -->
    <main-nav v-if="!isInDashboard" />
    <v-main id="main-content" style="margin-bottom: 0 !important">
      <router-view v-slot="{ Component }">
        <component :is="route.meta.layout || 'div'">
          <component :is="Component" />
        </component>
      </router-view>
    </v-main>

    <NotifyAlertContainer />
    <GlobalSearchModal />
    <KeyboardShortcutsModal />
    <FloatingCartButton />
    <CookieConsentBanner />

    <!-- Dev Tools (only in development) -->
    <template v-if="isDevEnvironment">
      <DevPanel />
      <DebugOverlay />
    </template>
  </v-app>
</template>

<style>
  @import '@fontsource/mukta/devanagari.css';

  * {
    font-family: 'Mukta', normal, sans-serif !important;
  }

  .skip-link {
    position: absolute;
    left: -999px;
    top: -999px;
    padding: 8px 12px;
    background: #00b0ff;
    color: white;
    border-radius: 6px;
    z-index: 9999;
  }
  .skip-link:focus {
    left: 12px;
    top: 12px;
    outline: 2px solid #fff;
    outline-offset: 2px;
  }
  .v-main {
    position: relative;
    z-index: 1;
  }
  .v-app-bar {
    position: relative;
    z-index: 2;
  }

  /* Prevent shadow clipping on landing page containers */
  .landing-layout .layout-content,
  .landing-layout .layout-content .v-container,
  .landing-layout .layout-content .v-sheet:not(.results-panel) {
    overflow: visible !important;
  }

  /* Scroll-reveal utility */
  .reveal {
    opacity: 0;
    transform: translateY(12px);
    transition:
      opacity 0.35s ease,
      transform 0.35s ease;
  }
  .revealed {
    opacity: 1;
    transform: translateY(0);
  }
  @media (prefers-reduced-motion: reduce) {
    .reveal {
      opacity: 1;
      transform: none;
      transition: none;
    }
  }
</style>
